import { BackrsUser, ProfileOptionAnswer, UserPermissions } from '@backrs_ui/common-ui';

import { ENVIRONMENT_URL } from '@backrs_ui/grow-ui/src/settings';

import { createApi } from '@reduxjs/toolkit/query/react';

import { userPermissionsTransformer, UIPermissionList } from './user.transformer';
import { baseQueryWithAuth } from '../baseQuery';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['UserPermissions'],
  endpoints: (builder) => ({
    fetchUserPermissions: builder.query<UIPermissionList | null, undefined>({
      query: () => ({
        url: `/users/current-user/permissions`,
        method: 'GET',
      }),
      transformResponse: (response: UserPermissions) => userPermissionsTransformer(response),
      providesTags: ['UserPermissions'],
    }),
    updateUserSettings: builder.mutation<BackrsUser, { user: BackrsUser }>({
      query: ({ user }) => ({
        url: `/users/${user.id}`,
        method: 'PUT',
        body: {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,

          // these fields may not always be present
          ...(user.phone ? { phone: user.phone } : undefined),
          ...(user.birthday ? { birthday: user.birthday } : undefined),
          ...(user.zip_code ? { zip_code: user.zip_code } : undefined),
          ...(user.password ? { password: user.password } : undefined),
          ...(user.password ? { confirmPassword: user.password } : undefined),
        },
      }),
    }),

    updateUserProfileOptionAnswer: builder.mutation<
      { success: boolean },
      { profileOptionAnswerKey: string; profileOptionAnswer: ProfileOptionAnswer }
    >({
      query: ({ profileOptionAnswerKey, profileOptionAnswer }) => ({
        url: `/profile`,
        method: 'PATCH',
        body: {
          [profileOptionAnswerKey]: [profileOptionAnswer],
        },
      }),
    }),

    trackUserActivity: builder.mutation<{ success: boolean }, string>({
      queryFn: async (access_token) => {
        try {
          const response = await fetch(`${ENVIRONMENT_URL}/users/activity/access`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to track user activity');
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
    }),
  }),
});

// Export the hooks and middleware
export const {
  useFetchUserPermissionsQuery,

  useUpdateUserSettingsMutation,
  useTrackUserActivityMutation,
  useUpdateUserProfileOptionAnswerMutation,

  util: { resetApiState },
} = userApi;
